import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, User, ArrowLeft, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [agreements, setAgreements] = useState({
    privacyPolicy: false,
    userAgreement: false,
    refundPolicy: false
  });
  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const { register, sendVerificationCode, verifyEmail } = useAuth();

  const { t, i18n } = useTranslation();

  // Detect current language from URL and update i18n
  React.useEffect(() => {
    const lang = location.pathname.includes('/en') ? 'en' : 'ru';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAgreementChange = (agreement) => {
    setAgreements(prev => ({
      ...prev,
      [agreement]: !prev[agreement]
    }));
  };

  const allAgreementsAccepted = Object.values(agreements).every(Boolean);

  const handleSendVerificationCode = async () => {
    if (!formData.email) {
      toast.error(t('errorMessages.enterEmail'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(t('errorMessages.invalidEmail'));
      return;
    }

    setVerificationLoading(true);
    try {
      console.log('Attempting to send verification code to:', formData.email);

      if (typeof sendVerificationCode !== 'function') {
        throw new Error('sendVerificationCode is not a function');
      }

      const result = await sendVerificationCode(formData.email);
      console.log('Send verification result:', result);
      toast.success(i18n.language === 'ru' ? t('register.successMessage') : t('register.successMessage'));
    } catch (error) {
      console.error('=== Detailed Verification Error ===');
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Error object:', error);

      if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
        toast.error(t('errorMessages.verificationNetworkError'));
      } else {
        toast.error(error.response?.data?.detail || error.message || t('errorMessages.verificationError'));
      }
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      toast.error(t('errorMessages.enterVerificationCode'));
      return;
    }

    setVerificationLoading(true);
    try {
      await verifyEmail(formData.email, verificationCode);
      setIsVerified(true);
      toast.success(i18n.language === 'ru' ? t('register.emailVerified') : t('register.emailVerified'));
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || t('errorMessages.invalidVerificationCode'));
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error(t('errorMessages.enterName'));
      return;
    }

    if (!formData.email) {
      toast.error(t('errorMessages.enterEmail'));
      return;
    }

    if (!formData.password) {
      toast.error(t('errorMessages.enterPassword'));
      return;
    }

    if (!allAgreementsAccepted) {
      toast.error(t('errorMessages.agreeToAll'));
      return;
    }

    if (!isVerified) {
      toast.error(t('errorMessages.verifyEmail'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('errorMessages.passwordsDontMatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('errorMessages.passwordLength'));
      return;
    }

    setLoading(true);

    try {
      await register(
        formData.email,
        formData.password,
        formData.name
      );
      toast.success(t('register.successMessage'));
      navigate('/klkt-segment');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || t('errorMessages.registrationError'));
    } finally {
      setLoading(false);
    }
  };

  // Функция для рендеринга ссылок в тексте соглашений
  const renderAgreementText = (key) => {
    const text = t(key);
    const parts = text.split(/<link>(.*?)<\/link>/);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // Это текст ссылки
        return (
          <Link
            key={index}
            to="/privacy-policy"
            className="text-[#C41E3A] font-bold hover:underline"
            target="_blank"
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  const handleLanguageSwitch = () => {
    const newLang = i18n.language === 'ru' ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
    if (newLang === 'ru') {
      navigate('/register');
    } else {
      navigate('/register/en');
    }
  };

  const backToHomePath = i18n.language === 'ru' ? '/' : '/en';

  return (
    <div className="min-h-screen paper-texture flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="flex justify-between items-center mb-8">
          <Link to={backToHomePath} className="inline-flex items-center gap-2 hover:text-[#C41E3A] transition-colors" data-testid="back-home-link">
            <ArrowLeft size={20} />
            {t('register.backToHome')}
          </Link>
          
          {/* Language Switcher */}
          <button
            onClick={handleLanguageSwitch}
            className="flex items-center gap-2 text-lg hover:text-[#C41E3A] transition-colors"
            aria-label={i18n.language === 'ru' ? t('language.switchTo') : t('language.switchTo')}
          >
            <Globe size={20} />
            <span className="hidden sm:inline">
              {i18n.language === 'ru' ? 'EN' : 'RU'}
            </span>
            <span className="sm:hidden">
              {i18n.language === 'ru' ? t('language.en') : t('language.ru')}
            </span>
          </button>
        </div>

        <div className="vintage-border bg-[#F5F1E8] p-8 engraving-texture">
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="register-title"
            >
              {t('register.title')}
            </h1>
            <p className="text-gray-600" data-testid="register-subtitle">{t('register.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="block mb-2 font-semibold" data-testid="name-label">
                <User className="inline mr-2" size={18} />
                {t('register.nameLabel')} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                placeholder={t('register.namePlaceholder')}
                required
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block mb-2 font-semibold" data-testid="email-label">
                <Mail className="inline mr-2" size={18} />
                {t('register.emailLabel')} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                placeholder={t('register.emailPlaceholder')}
                required
                data-testid="email-input"
              />
            </div>

            {/* Email Verification Section */}
            {!isVerified && (
              <div className="space-y-3 p-4 border-2 border-dashed border-gray-400 rounded">
                <p className="text-sm font-semibold text-center">{t('register.verificationTitle')}</p>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSendVerificationCode}
                    disabled={verificationLoading}
                    className="flex-1 engraved-button engraved-button-red text-sm disabled:opacity-50"
                  >
                    {verificationLoading ? t('register.sendingCode') : t('register.getCodeButton')}
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold">
                    {t('register.verificationCodeLabel')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="flex-1 px-3 py-2 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                      placeholder={t('register.verificationCodePlaceholder')}
                      maxLength={6}
                      data-testid="verification-code-input"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyCode}
                      disabled={verificationLoading || !verificationCode}
                      className="px-4 engraved-button engraved-button-red text-sm disabled:opacity-50"
                    >
                      {verificationLoading ? t('register.verifyingCode') : t('register.verifyButton')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isVerified && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 text-center rounded">
                {t('register.emailVerified')}
              </div>
            )}

            <div>
              <label className="block mb-2 font-semibold" data-testid="password-label">
                <Lock className="inline mr-2" size={18} />
                {t('register.passwordLabel')} *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                placeholder={t('register.passwordPlaceholder')}
                required
                minLength={6}
                data-testid="password-input"
              />
            </div>

            <div>
              <label className="block mb-2 font-semibold" data-testid="confirm-password-label">
                <Lock className="inline mr-2" size={18} />
                {t('register.confirmPasswordLabel')} *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-black bg-[#F5F1E8] focus:outline-none focus:border-[#C41E3A] transition-colors"
                placeholder={t('register.confirmPasswordPlaceholder')}
                required
                data-testid="confirm-password-input"
              />
            </div>

            {/* Соглашения с документами */}
            <div className="space-y-3 mt-4 p-4 border-2 border-gray-300 rounded bg-white">
              <h3 className="font-semibold text-center mb-3">{t('register.agreementsTitle')} *</h3>

              {/* Политика конфиденциальности */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="privacy-policy"
                  checked={agreements.privacyPolicy}
                  onChange={() => handleAgreementChange('privacyPolicy')}
                  className="mt-1"
                  data-testid="privacy-policy-checkbox"
                />
                <label htmlFor="privacy-policy" className="text-sm">
                  {renderAgreementText('register.privacyPolicy')}
                </label>
              </div>

              {/* Пользовательское соглашение */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="user-agreement"
                  checked={agreements.userAgreement}
                  onChange={() => handleAgreementChange('userAgreement')}
                  className="mt-1"
                  data-testid="user-agreement-checkbox"
                />
                <label htmlFor="user-agreement" className="text-sm">
                  {renderAgreementText('register.userAgreement')}
                </label>
              </div>

              {/* Политика возвратов */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="refund-policy"
                  checked={agreements.refundPolicy}
                  onChange={() => handleAgreementChange('refundPolicy')}
                  className="mt-1"
                  data-testid="refund-policy-checkbox"
                />
                <label htmlFor="refund-policy" className="text-sm">
                  {renderAgreementText('register.refundPolicy')}
                </label>
              </div>

              {!allAgreementsAccepted && (
                <p className="text-xs text-red-600 mt-2">
                  {t('register.agreementsRequired')}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full engraved-button engraved-button-red mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !isVerified || !allAgreementsAccepted}
              data-testid="register-submit-btn"
            >
              {loading ? t('register.registering') : t('register.registerButton')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600" data-testid="login-prompt">
              {t('register.loginPrompt')}
              <Link
                to={i18n.language === 'ru' ? "/login" : "/login/en"}
                className="text-[#C41E3A] font-bold hover:underline"
                data-testid="login-link"
              >
                {t('register.loginLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;